/**
 * CleanedDataReview.js
 * Main container component for Phase 4: Post-Cleaning Data Review
 * 
 * Features:
 * - Run quality checks on cleaned data
 * - Side-by-side comparison dashboard
 * - AI Quality Dashboard for cleaned data
 * - Back-check reference (read-only)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Database,
  RefreshCw,
  Loader,
  BarChart2,
  FileText,
  Target,
  ArrowLeft,
  Play,
  Eye,
  TrendingUp,
  Shield,
  // Ship 4: icons for the additional detector cards
  MapPin,           // geolocation
  GitBranch,        // survey flow
  Copy,             // text clustering similar pairs
  MessageSquare,    // sentinel phrases
  Link as LinkIcon, // gated fields
  Activity,         // straight-lining
  Code2,            // encoding
  Shuffle,          // backcheck
  Scissors,         // dropped cols
  Tag,              // flag cols added
  Trash2,           // rows dropped
} from 'lucide-react';
import ComparisonDashboard from './ComparisonDashboard';
import AILoadingOverlay from '../common/AILoadingOverlay';

const CleanedDataReview = ({
  projectId,
  rawCheckResults = {},
  backcheckResults = null,
  onNavigate = null,
  onCleanedAnalysisComplete = null,
  xlsformData = null,           // NEW: Parsed XLSForm data
  xlsformMatchingResult = null,  // NEW: Variable mapping
  selectedChecks = []           // Checks selected in ConfigureDetectors
}) => {
  const { isDarkMode } = useTheme();
  
  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;
  
  // State
  const [activeTab, setActiveTab] = useState('comparison'); // 'comparison' | 'quality-checks' | 'backcheck-reference'
  const [cleanedCheckResults, setCleanedCheckResults] = useState({});
  const [cleanedData, setCleanedData] = useState(null);
  const [hasCleanedData, setHasCleanedData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
  const [error, setError] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('idle'); // 'idle' | 'running' | 'complete' | 'error'

  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';

  const getAuthHeaders = useCallback(() => {
    let userEmail = '';
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      userEmail = userInfo.email || '';
    } catch (e) {
      userEmail = localStorage.getItem('userEmail') || '';
    }
    return {
      'user_email': userEmail,
      'User-Email': userEmail,
      'Content-Type': 'application/json'
    };
  }, []);

  // Check if cleaned data exists
  const checkCleanedDataExists = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cleaning/cleaned-data/${projectId}?preview_only=true`,
        { headers: getAuthHeaders() }
      );
      
      const data = await response.json();
      
      if (data.success && data.has_cleaned_data) {
        setHasCleanedData(true);
        setCleanedData({
          columns: data.columns,
          column_types: data.column_types,
          total_rows: data.total_rows,
          preview: data.data
        });
      } else {
        setHasCleanedData(false);
        setCleanedData(null);
      }
    } catch (err) {
      console.error('Error checking cleaned data:', err);
      setHasCleanedData(false);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, API_BASE, getAuthHeaders]);

  // Fetch existing cleaned analysis results
  const fetchCleanedResults = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cleaning/cleaned-analysis/${projectId}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.results) {
          setCleanedCheckResults(data.results);
          setAnalysisStatus('complete');
        }
      }
    } catch (err) {
      console.error('Error fetching cleaned results:', err);
    }
  }, [projectId, API_BASE, getAuthHeaders]);

  // Run quality analysis on cleaned data
  const runCleanedAnalysis = async () => {
    if (!projectId || !hasCleanedData) return;
    
    setIsRunningAnalysis(true);
    setAnalysisStatus('running');
    setError(null);
    
    try {
      // Extract duration settings from raw check results to ensure consistent analysis
      const durationSettings = {};
      if (rawCheckResults?.duration) {
        const dur = rawCheckResults.duration;
        const durOverall = dur.overall || dur;
        console.log('Raw duration results structure:', JSON.stringify(durOverall, null, 2).substring(0, 1000));
        
        // Extract duration_range
        let durationRange = null;
        if (durOverall.duration_range?.min !== undefined && durOverall.duration_range?.max !== undefined) {
          durationRange = durOverall.duration_range;
          console.log('Found duration_range at durOverall.duration_range');
        }
        
        if (durationRange) {
          durationSettings.duration_range = durationRange;
          console.log('Using duration_range:', durationRange);
        }
        
        // Extract duration variables configuration (how duration was calculated)
        // This tells the backend whether to use timestamps or a pre-calculated column
        if (durOverall.duration_type === 'calculated_from_timestamps') {
          // User configured start/end time variables
          durationSettings.duration_config = {
            type: 'timestamps',
            start_variable: durOverall.start_variable,
            end_variable: durOverall.end_variable
          };
          console.log('Duration config: calculated from timestamps', durationSettings.duration_config);
        } else if (durOverall.variable_analyzed || durOverall.column_used) {
          // User configured a pre-calculated duration column
          durationSettings.duration_config = {
            type: 'column',
            column: durOverall.variable_analyzed || durOverall.column_used
          };
          console.log('Duration config: pre-calculated column', durationSettings.duration_config);
        }
      } else {
        console.warn('No duration data in rawCheckResults');
      }
      
      // Build XLSForm settings if available (for skip logic and constraint validation)
      const xlsformSettings = {};
      if (xlsformData?.parseResult) {
        xlsformSettings.xlsform_data = xlsformData.parseResult;
        console.log('Including XLSForm data in analysis request');
        
        // Include variable mapping if available
        if (xlsformMatchingResult?.matches) {
          xlsformSettings.variable_mapping = xlsformMatchingResult.matches;
        }
      }
      
      // Ship 4: re-run ALL detectors that were originally selected, not just
      // the 4 hardcoded ones. The backend silently skips detectors it doesn't
      // implement (returns _not_run_on_cleaned: true so the comparison
      // dashboard can render "Not measured" instead of a misleading 0).
      const ALL_KNOWN_CHECKS = [
        'duplicates', 'missingness', 'outliers', 'duration',
        'geolocation', 'survey_flow', 'text_clustering',
        'sentinel_phrases', 'gated_fields', 'probe_depth', 'straight_lining',
        'backcheck', 'encoding',
      ];
      const checksToRun = selectedChecks.length > 0
        ? selectedChecks.filter(c => ALL_KNOWN_CHECKS.includes(c))
        : ALL_KNOWN_CHECKS;
      
      const requestBody = {
        checks: checksToRun,
        ...durationSettings,
        ...xlsformSettings,
        // Ship 4: forward raw analysis envelope so the backend can extract
        // per-detector parameters (sentinel regex, gate rules, scale vars,
        // probe thresholds, etc.) and re-run with the SAME config — apples-
        // to-apples comparison instead of defaults.
        raw_quality_results: rawCheckResults,
      };

      // Ship 4: include column_types and key_variables when available so
      // type-aware checks (straight_lining auto-detection, etc.) and ID-aware
      // enrichment (collector/respondent/date columns) can work on cleaned data.
      if (cleanedData?.column_types) {
        requestBody.column_types = cleanedData.column_types;
      }
      // Try to lift key variables out of any raw check that has them
      // (sentinel/gated/probe/straight all carry these in overall.parameters).
      try {
        const probeForKeyVars = (rawCheckResults?.sentinel_phrases?.overall?.parameters)
          || (rawCheckResults?.gated_fields?.overall?.parameters)
          || (rawCheckResults?.probe_depth?.overall?.parameters)
          || (rawCheckResults?.straight_lining?.overall?.parameters)
          || {};
        const keyVars = {};
        if (probeForKeyVars.respondent_id_column)      keyVars.respondent_id     = probeForKeyVars.respondent_id_column;
        if (probeForKeyVars.data_collector_column)     keyVars.collector_id      = probeForKeyVars.data_collector_column;
        if (probeForKeyVars.interview_date_column)     keyVars.interview_date    = probeForKeyVars.interview_date_column;
        if (Object.keys(keyVars).length > 0) {
          requestBody.key_variables = keyVars;
        }
      } catch (e) {
        // Best-effort — proceed without key vars if anything throws
      }
      
      // Extract outlier bounds from raw analysis to use same thresholds on cleaned data
      // This ensures apples-to-apples comparison instead of recalculating IQR
      if (rawCheckResults?.outliers) {
        const outlierBounds = {};
        const rawOutliers = rawCheckResults.outliers.overall || rawCheckResults.outliers;
        
        // Extract bounds for each variable
        Object.entries(rawOutliers).forEach(([varName, varData]) => {
          if (varData && typeof varData === 'object') {
            // Get constraint bounds (from XLSForm) or statistical bounds (IQR/zscore)
            if (varData.constraint_bounds) {
              outlierBounds[varName] = {
                method: 'constraint',
                bounds: varData.constraint_bounds
              };
            } else if (varData.statistical_bounds) {
              outlierBounds[varName] = {
                method: 'statistical',
                bounds: varData.statistical_bounds
              };
            } else if (varData.q25 !== undefined && varData.q75 !== undefined) {
              // Reconstruct IQR bounds from quartiles
              const q25 = varData.q25;
              const q75 = varData.q75;
              const iqr = q75 - q25;
              outlierBounds[varName] = {
                method: 'iqr',
                bounds: {
                  lower: q25 - 1.5 * iqr,
                  upper: q75 + 1.5 * iqr,
                  q25, q75, iqr
                }
              };
            }
          }
        });
        
        if (Object.keys(outlierBounds).length > 0) {
          requestBody.outlier_bounds = outlierBounds;
          console.log('Passing outlier bounds from raw analysis:', Object.keys(outlierBounds).length, 'variables');
        }
      }
      
      console.log('Sending analysis request with:', JSON.stringify(requestBody, null, 2).substring(0, 800));
      
      const response = await fetch(
        `${API_BASE}/api/cleaning/analyze-cleaned/${projectId}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(requestBody)
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setCleanedCheckResults(data.results || {});
        setAnalysisStatus('complete');
        
        if (onCleanedAnalysisComplete) {
          onCleanedAnalysisComplete(data.results);
        }
      } else {
        setError(data.detail || 'Failed to run analysis on cleaned data');
        setAnalysisStatus('error');
      }
    } catch (err) {
      setError(`Error running analysis: ${err.message}`);
      setAnalysisStatus('error');
    } finally {
      setIsRunningAnalysis(false);
    }
  };

  useEffect(() => {
    checkCleanedDataExists();
    fetchCleanedResults();
  }, [checkCleanedDataExists, fetchCleanedResults]);

  // Tabs configuration
  const tabs = [
    {
      id: 'comparison',
      label: 'Comparison Dashboard',
      icon: Target,
      description: 'Side-by-side before/after metrics'
    },
    {
      id: 'quality-checks',
      label: 'Quality Checks',
      icon: Shield,
      description: 'Detailed quality check results on cleaned data',
      disabled: !hasCleanedData || Object.keys(cleanedCheckResults).length === 0
    }
  ];

  // Styles
  const styles = {
    container: {
      backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F599',
      minHeight: '100%',
      width: '100%',
      maxWidth: '100%',
      padding: isMobile ? '12px' : '32px 40px 24px 0px',
      marginLeft: isMobile ? '0' : '50px',
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      overflowX: 'hidden',
      boxSizing: 'border-box',
    },
    header: {
      padding: isMobile ? '16px 18px' : '20px 24px',
      margin: isMobile ? '0 12px 12px 12px' : '0 32px 16px 32px',
      borderRadius: '8px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`,
      backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
      boxSizing: 'border-box',
    },
    headerContent: {
      width: '100%',
    },
    title: {
      fontSize: isMobile ? '17px' : '20px',
      fontWeight: '500',
      color: isDarkMode ? '#f0ede4' : '#1a1a1a',
      marginBottom: '2px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      letterSpacing: '-0.02em',
      lineHeight: 1.25,
    },
    subtitle: {
      fontSize: isMobile ? '11px' : '12px',
      color: isDarkMode ? '#a8a49a' : '#555550',
      marginBottom: isMobile ? '10px' : '14px',
      opacity: 0.8,
      lineHeight: 1.4,
    },
    statusBar: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
      rowGap: '8px',
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 12px',
      borderRadius: '100px',
      fontSize: '11px',
      fontWeight: '500',
      letterSpacing: '-0.005em',
      border: `0.5px solid transparent`,
      whiteSpace: 'nowrap',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '7px 14px',
      borderRadius: '100px',
      border: 'none',
      fontSize: '11.5px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      letterSpacing: '-0.005em',
      fontFamily: 'inherit',
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    tabsContainer: {
      display: 'inline-flex',
      gap: '2px',
      padding: '3px',
      margin: isMobile ? '12px' : '16px 32px',
      borderRadius: '11px',
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.055)' : 'rgba(0, 0, 0, 0.045)',
      border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.07)'}`,
      overflowX: 'auto',
      flexWrap: 'nowrap',
      maxWidth: 'calc(100% - 64px)',
    },
    tab: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 13px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '12.5px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      whiteSpace: 'nowrap',
      letterSpacing: '-0.005em',
      fontFamily: 'inherit',
      backgroundColor: 'transparent',
      flexShrink: 0,
    },
    content: {
      padding: '0',
    },
    noDataContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      padding: isMobile ? '24px 12px' : '40px',
      textAlign: 'center',
    },
    noDataCard: {
      backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
      borderRadius: '8px',
      padding: isMobile ? '24px 16px' : '40px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`,
      maxWidth: '500px',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '300px',
      gap: '16px',
    },
    errorBanner: {
      margin: isMobile ? '16px 12px' : '16px 32px',
      padding: '10px 14px',
      backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.06)',
      color: isDarkMode ? '#F87171' : '#991b1b',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      border: `0.5px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.2)'}`,
      fontSize: '12px',
    },
  };

  // Render loading state
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <Loader size={28} style={{ animation: 'spin 1s linear infinite', color: isDarkMode ? '#a8a49a' : '#6B7280' }} />
          <p style={{ color: isDarkMode ? '#a8a49a' : '#6B7280' }}>
            Checking for cleaned data...
          </p>
        </div>
      </div>
    );
  }

  // Render no cleaned data state
  if (!hasCleanedData) {
    return (
      <div style={styles.container}>
        <div style={styles.noDataContainer}>
          <div style={styles.noDataCard}>
            <Database size={36} style={{ color: '#D97706', marginBottom: '14px', opacity: 0.85 }} />
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: isDarkMode ? '#E5E7EB' : '#222222',
              marginBottom: '12px'
            }}>
              No Cleaned Data Available
            </h2>
            <p style={{
              fontSize: '13px',
              color: isDarkMode ? '#a8a49a' : '#6B7280',
              marginBottom: '12px',
              lineHeight: '1.6'
            }}>
              You need to run a cleaning script first before you can review the cleaned data. 
              Go to the Data Cleaning Studio to generate and execute a cleaning script.
            </p>
            <button
              style={{
                ...styles.actionButton,
                backgroundColor: isDarkMode ? '#f0ede4' : '#1a1a1a',
                color: isDarkMode ? '#0D0E0E' : '#F8F5EE',
                marginTop: '8px'
              }}
              onClick={() => onNavigate && onNavigate('cleaning-studio')}
            >
              <ArrowLeft size={13} />
              Go to Cleaning Studio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Loading Overlay for Analysis */}
      <AILoadingOverlay
        isVisible={isRunningAnalysis}
        context="quality_analysis"
        title="Analyzing Cleaned Data"
        subtitle="Running quality checks on your cleaned dataset"
        realTimeStats={{
          totalRows: cleanedData?.total_rows || 0,
          columnsAnalyzed: cleanedData?.column_types 
            ? Object.keys(cleanedData.column_types).length 
            : 0,
        }}
        showDetailedLogs={true}
      />

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>
            <CheckCircle size={18} style={{ color: '#10B981', opacity: 0.9 }} />
            Cleaned Data Review
          </h1>
          <p style={styles.subtitle}>
            Analyze and compare your data quality before and after cleaning
          </p>
          
          <div style={styles.statusBar}>
            {/* Cleaned Data Status */}
            <div style={{
              ...styles.statusBadge,
              backgroundColor: 'transparent',
              color: '#10B981',
              borderColor: 'rgba(16, 185, 129, 0.3)',
            }}>
              <CheckCircle size={12} />
              Cleaned Data Available
              {cleanedData?.total_rows && ` (${cleanedData.total_rows.toLocaleString()} rows)`}
            </div>
            
            {/* Analysis Status */}
            {analysisStatus === 'complete' && Object.keys(cleanedCheckResults).length > 0 && (
              <div style={{
                ...styles.statusBadge,
                backgroundColor: 'transparent',
                color: '#10B981',
                borderColor: 'rgba(16, 185, 129, 0.3)',
              }}>
                <Shield size={12} />
                Quality Checks Complete
              </div>
            )}
            
            {analysisStatus === 'idle' && (
              <div style={{
                ...styles.statusBadge,
                backgroundColor: 'transparent',
                color: isDarkMode ? '#a8a49a' : '#6B7280',
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              }}>
                <AlertTriangle size={12} />
                Quality checks not run yet
              </div>
            )}
            
            {/* Run Analysis Button */}
            <button
              style={{
                ...styles.actionButton,
                backgroundColor: analysisStatus === 'complete'
                  ? (isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)')
                  : (isDarkMode ? '#f0ede4' : '#1a1a1a'),
                color: analysisStatus === 'complete'
                  ? (isDarkMode ? '#E5E7EB' : '#222222')
                  : (isDarkMode ? '#0D0E0E' : '#F8F5EE'),
                border: analysisStatus === 'complete'
                  ? `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`
                  : 'none',
                marginLeft: isMobile ? '0' : 'auto',
                opacity: isRunningAnalysis ? 0.6 : 1,
              }}
              onClick={runCleanedAnalysis}
              disabled={isRunningAnalysis}
            >
              {isRunningAnalysis ? (
                <>
                  <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
                  Running Analysis...
                </>
              ) : analysisStatus === 'complete' ? (
                <>
                  <RefreshCw size={13} />
                  Re-run Analysis
                </>
              ) : (
                <>
                  <Play size={13} />
                  Run Quality Analysis
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div style={styles.errorBanner}>
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const isDisabled = tab.disabled;
          
          return (
            <button
              key={tab.id}
              style={{
                ...styles.tab,
                backgroundColor: isActive
                  ? (isDarkMode ? '#17181a' : '#FFFFFF')
                  : 'transparent',
                color: isActive
                  ? (isDarkMode ? '#ececec' : '#1a1a1a')
                  : (isDarkMode ? '#6d7178' : '#8a8f98'),
                fontWeight: isActive ? 600 : 500,
                boxShadow: isActive
                  ? (isDarkMode
                      ? '0 1px 2px rgba(0,0,0,0.4)'
                      : '0 1px 2px rgba(0,0,0,0.08), 0 1px 6px rgba(0,0,0,0.06)')
                  : 'none',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.4 : 1,
              }}
              onClick={() => !isDisabled && setActiveTab(tab.id)}
              disabled={isDisabled}
              title={tab.description}
            >
              <Icon size={13} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Comparison Dashboard */}
        {activeTab === 'comparison' && (
          <ComparisonDashboard
            projectId={projectId}
            rawCheckResults={rawCheckResults}
            cleanedCheckResults={cleanedCheckResults}
            onRefresh={runCleanedAnalysis}
            selectedChecks={selectedChecks}
          />
        )}

        {/* Quality Checks Tab */}
        {activeTab === 'quality-checks' && (
          <div style={{ padding: isMobile ? '16px 12px' : '24px 32px' }}>
            {Object.keys(cleanedCheckResults).length > 0 ? (
              <div>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  color: isDarkMode ? '#f0ede4' : '#1a1a1a',
                  marginBottom: '6px',
                  letterSpacing: '-0.02em'
                }}>
                  Quality Check Results (Cleaned Data)
                </h2>
                <p style={{
                  color: isDarkMode ? '#a8a49a' : '#6B7280',
                  marginBottom: '12px'
                }}>
                  Detailed breakdown of quality checks run on your cleaned dataset.
                </p>
                
                {/* Quality Checks Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
                  gap: '16px'
                }}>
                  
                  {/* Missingness Card */}
                  {cleanedCheckResults.missingness && (
                    <div style={{
                      backgroundColor: isDarkMode ? '#111114' : '#ffffff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`
                    }}>
                      <h3 style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: isDarkMode ? '#f0ede4' : '#1a1a1a',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        letterSpacing: '-0.01em'
                      }}>
                        <BarChart2 size={14} style={{ color: '#D97706' }} />
                        Missing Values Analysis
                      </h3>
                      
                      {cleanedCheckResults.missingness.summary && (
                        <div style={{
                          padding: '12px 14px',
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                          borderRadius: '6px',
                          border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                          marginBottom: '12px'
                        }}>
                          <div style={{ fontSize: '22px', fontWeight: '600', color: '#D97706', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                            {cleanedCheckResults.missingness.summary.overall_missing_pct?.toFixed(2) || '0'}%
                          </div>
                          <div style={{ fontSize: '13px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>
                            Overall Missing Rate
                          </div>
                        </div>
                      )}
                      
                      {cleanedCheckResults.missingness.overall?.per_variable && (
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}` }}>
                                <th style={{ textAlign: 'left', padding: '8px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Variable</th>
                                <th style={{ textAlign: 'right', padding: '8px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Missing %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(cleanedCheckResults.missingness.overall.per_variable)
                                .filter(([_, v]) => v.missing_percentage > 0)
                                .sort((a, b) => b[1].missing_percentage - a[1].missing_percentage)
                                .slice(0, 10)
                                .map(([varName, data]) => (
                                  <tr key={varName} style={{ borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}` }}>
                                    <td style={{ padding: '8px 4px', color: isDarkMode ? '#E5E7EB' : '#374151', fontSize: '12px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                                      {varName.length > 25 ? varName.substring(0, 25) + '...' : varName}
                                    </td>
                                    <td style={{ 
                                      textAlign: 'right', 
                                      padding: '8px 4px',
                                      color: data.missing_percentage > 50 ? '#EF4444' : data.missing_percentage > 20 ? '#D97706' : '#10B981'
                                    }}>
                                      {data.missing_percentage.toFixed(1)}%
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                          {Object.keys(cleanedCheckResults.missingness.overall.per_variable).length > 10 && (
                            <p style={{ fontSize: '12px', color: isDarkMode ? '#6B7280' : '#9CA3AF', marginTop: '8px', textAlign: 'center' }}>
                              Showing top 10 of {Object.keys(cleanedCheckResults.missingness.overall.per_variable).length} variables
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Outliers Card */}
                  {cleanedCheckResults.outliers && (
                    <div style={{
                      backgroundColor: isDarkMode ? '#111114' : '#ffffff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`
                    }}>
                      <h3 style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: isDarkMode ? '#f0ede4' : '#1a1a1a',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        letterSpacing: '-0.01em'
                      }}>
                        <AlertTriangle size={14} style={{ color: '#EF4444' }} />
                        Statistical Outliers
                      </h3>
                      
                      <div style={{
                        padding: '12px 14px',
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                        borderRadius: '6px',
                        border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                        marginBottom: '12px'
                      }}>
                        <div style={{ fontSize: '22px', fontWeight: '600', color: '#EF4444', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                          {cleanedCheckResults.outliers.summary?.total_outliers || 
                           cleanedCheckResults.outliers.total_outliers || 0}
                        </div>
                        <div style={{ fontSize: '13px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>
                          Total Outliers Detected
                        </div>
                      </div>
                      
                      {cleanedCheckResults.outliers.column_details && (
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}` }}>
                                <th style={{ textAlign: 'left', padding: '8px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Variable</th>
                                <th style={{ textAlign: 'right', padding: '8px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Count</th>
                                <th style={{ textAlign: 'right', padding: '8px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Bounds</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(cleanedCheckResults.outliers.column_details)
                                .filter(([_, v]) => v.outlier_count > 0)
                                .sort((a, b) => b[1].outlier_count - a[1].outlier_count)
                                .slice(0, 10)
                                .map(([varName, data]) => (
                                  <tr key={varName} style={{ borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}` }}>
                                    <td style={{ padding: '8px 4px', color: isDarkMode ? '#E5E7EB' : '#374151', fontSize: '12px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                                      {varName.length > 20 ? varName.substring(0, 20) + '...' : varName}
                                    </td>
                                    <td style={{ textAlign: 'right', padding: '8px 4px', color: '#EF4444', fontWeight: '600' }}>
                                      {data.outlier_count}
                                    </td>
                                    <td style={{ textAlign: 'right', padding: '8px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280', fontSize: '11px' }}>
                                      [{data.lower_bound?.toFixed(1) || '?'}, {data.upper_bound?.toFixed(1) || '?'}]
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Duplicates Card */}
                  {cleanedCheckResults.duplicates && (
                    <div style={{
                      backgroundColor: isDarkMode ? '#111114' : '#ffffff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`
                    }}>
                      <h3 style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: isDarkMode ? '#f0ede4' : '#1a1a1a',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        letterSpacing: '-0.01em'
                      }}>
                        <Database size={14} style={{ color: '#10B981' }} />
                        Duplicate Analysis
                      </h3>
                      
                      <div style={{
                        padding: '12px 14px',
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                        borderRadius: '6px',
                        border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                        marginBottom: '12px'
                      }}>
                        <div style={{ fontSize: '22px', fontWeight: '600', color: '#10B981', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                          {cleanedCheckResults.duplicates.total_duplicates || 
                           cleanedCheckResults.duplicates.duplicate_count || 0}
                        </div>
                        <div style={{ fontSize: '13px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>
                          Duplicate Values Found
                        </div>
                      </div>
                      
                      {cleanedCheckResults.duplicates.message && (
                        <p style={{ 
                          fontSize: '13px', 
                          color: isDarkMode ? '#a8a49a' : '#6B7280',
                          padding: '12px',
                          backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.06)',
                          borderRadius: '8px'
                        }}>
                          {cleanedCheckResults.duplicates.message}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Duration Card */}
                  {cleanedCheckResults.duration && (
                    <div style={{
                      backgroundColor: isDarkMode ? '#111114' : '#ffffff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`
                    }}>
                      <h3 style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: isDarkMode ? '#f0ede4' : '#1a1a1a',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        letterSpacing: '-0.01em'
                      }}>
                        <Target size={14} style={{ color: '#8B5CF6' }} />
                        Interview Duration
                      </h3>
                      
                      {cleanedCheckResults.duration.message ? (
                        <div style={{
                          padding: '12px 14px',
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                          border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                          borderRadius: '6px',
                          color: isDarkMode ? '#a8a49a' : '#6B7280',
                          textAlign: 'center',
                          fontSize: '12px'
                        }}>
                          {cleanedCheckResults.duration.message}
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <div style={{
                              padding: '12px 14px',
                              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                              border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                              borderRadius: '6px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '22px', fontWeight: '600', color: '#D97706', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                                {cleanedCheckResults.duration.too_short_count || cleanedCheckResults.duration.short_count || 0}
                              </div>
                              <div style={{ fontSize: '10px', color: isDarkMode ? '#a8a49a' : '#6B7280', textTransform: 'lowercase', letterSpacing: '0.06em', marginTop: '2px' }}>Too Short</div>
                            </div>
                            <div style={{
                              padding: '12px 14px',
                              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                              border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                              borderRadius: '6px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '22px', fontWeight: '600', color: '#EF4444', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                                {cleanedCheckResults.duration.too_long_count || cleanedCheckResults.duration.long_count || 0}
                              </div>
                              <div style={{ fontSize: '10px', color: isDarkMode ? '#a8a49a' : '#6B7280', textTransform: 'lowercase', letterSpacing: '0.06em', marginTop: '2px' }}>Too Long</div>
                            </div>
                          </div>
                          
                          {cleanedCheckResults.duration.summary && (
                            <div style={{ fontSize: '13px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>
                              <p>Valid Range: {cleanedCheckResults.duration.summary.lower_bound || 5} - {cleanedCheckResults.duration.summary.upper_bound || 180} minutes</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* ============================================================
                      Ship 4: Cards for the 9 additional detectors. Each renders
                      ONLY when there's a result on the cleaned side. If the
                      backend marked _not_run_on_cleaned, we render a small
                      "Not measured" placeholder so users understand the gap.
                      ============================================================ */}
                  {(() => {
                    const cardWrapStyle = {
                      backgroundColor: isDarkMode ? '#111114' : '#ffffff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`
                    };
                    const titleStyle = {
                      fontSize: '13px',
                      fontWeight: '500',
                      color: isDarkMode ? '#f0ede4' : '#1a1a1a',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      letterSpacing: '-0.01em'
                    };
                    const bigNumberWrap = {
                      padding: '12px 14px',
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '6px',
                      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                      marginBottom: '12px'
                    };
                    const bigNumberStyle = (color) => ({
                      fontSize: '22px',
                      fontWeight: '600',
                      color,
                      letterSpacing: '-0.01em',
                      fontVariantNumeric: 'tabular-nums'
                    });
                    const subtleLabel = { fontSize: '13px', color: isDarkMode ? '#a8a49a' : '#6B7280' };
                    const notMeasuredBlock = (reason) => (
                      <div style={{
                        padding: '12px 14px',
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)',
                        border: `0.5px dashed ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                        borderRadius: '6px',
                        color: isDarkMode ? '#a8a49a' : '#6B7280',
                        fontSize: '12px',
                        fontStyle: 'italic'
                      }}>
                        Not measured on cleaned data{reason ? ` — ${reason}` : '.'}
                      </div>
                    );

                    // Helper to read a flagged-rows count from various v2 envelope shapes
                    const flaggedCount = (result) => {
                      if (!result) return 0;
                      const o = result.overall || result;
                      if (Array.isArray(o.flagged_rows)) return o.flagged_rows.length;
                      return o.total_flagged || o.flagged_count || o.respondents_flagged || 0;
                    };
                    const isNotRun = (result) => {
                      if (!result) return false;
                      if (result._not_run_on_cleaned) return true;
                      if (result.overall && result.overall._not_run_on_cleaned) return true;
                      return false;
                    };
                    const reasonOf = (result) => {
                      if (!result) return null;
                      return result._reason || result.overall?._reason || null;
                    };

                    // Generic top-N flagged-rows table for the coaching detectors
                    const flaggedTopN = (result, n = 5) => {
                      const o = result?.overall || result;
                      if (!Array.isArray(o?.flagged_rows) || o.flagged_rows.length === 0) return null;
                      // Pull top N by severity if available, else first N
                      const rows = o.flagged_rows.slice(0, n);
                      return (
                        <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '6px' }}>
                          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}` }}>
                                <th style={{ textAlign: 'left', padding: '6px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Variable</th>
                                <th style={{ textAlign: 'left', padding: '6px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Type</th>
                                <th style={{ textAlign: 'left', padding: '6px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Severity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, i) => (
                                <tr key={i} style={{ borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}` }}>
                                  <td style={{ padding: '6px 4px', color: isDarkMode ? '#E5E7EB' : '#374151', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                                    {(r.variable || r.var_name || r.column || '—').toString().slice(0, 22)}
                                  </td>
                                  <td style={{ padding: '6px 4px', color: isDarkMode ? '#a8a49a' : '#6B7280', fontSize: '11px' }}>
                                    {r.flag_type || r.type || '—'}
                                  </td>
                                  <td style={{ padding: '6px 4px', color: r.severity === 'critical' ? '#EF4444' : r.severity === 'high' ? '#D97706' : '#6B7280', fontSize: '11px' }}>
                                    {r.severity || '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {o.flagged_rows.length > n && (
                            <p style={{ fontSize: '11px', color: isDarkMode ? '#6B7280' : '#9CA3AF', marginTop: '6px', textAlign: 'center' }}>
                              Showing top {n} of {o.flagged_rows.length} flagged rows
                            </p>
                          )}
                        </div>
                      );
                    };

                    // Build cards array — each entry returns either a card or null
                    const detectorCards = [
                      cleanedCheckResults.geolocation && (
                        <div key="geo" style={cardWrapStyle}>
                          <h3 style={titleStyle}>
                            <MapPin size={14} style={{ color: '#0EA5E9' }} />
                            Geolocation
                          </h3>
                          {isNotRun(cleanedCheckResults.geolocation) ? notMeasuredBlock(reasonOf(cleanedCheckResults.geolocation))
                            : (
                              <>
                                <div style={bigNumberWrap}>
                                  <div style={bigNumberStyle('#0EA5E9')}>{flaggedCount(cleanedCheckResults.geolocation).toLocaleString()}</div>
                                  <div style={subtleLabel}>Suspicious GPS Points</div>
                                </div>
                              </>
                            )}
                        </div>
                      ),
                      cleanedCheckResults.survey_flow && (
                        <div key="flow" style={cardWrapStyle}>
                          <h3 style={titleStyle}>
                            <GitBranch size={14} style={{ color: '#3B82F6' }} />
                            Survey Flow
                          </h3>
                          {isNotRun(cleanedCheckResults.survey_flow) ? notMeasuredBlock(reasonOf(cleanedCheckResults.survey_flow))
                            : (
                              <div style={bigNumberWrap}>
                                <div style={bigNumberStyle('#3B82F6')}>{flaggedCount(cleanedCheckResults.survey_flow).toLocaleString()}</div>
                                <div style={subtleLabel}>Skip-Logic Violations</div>
                              </div>
                            )}
                        </div>
                      ),
                      cleanedCheckResults.text_clustering && (
                        <div key="text" style={cardWrapStyle}>
                          <h3 style={titleStyle}>
                            <Copy size={14} style={{ color: '#8B5CF6' }} />
                            Text Clustering
                          </h3>
                          {isNotRun(cleanedCheckResults.text_clustering) ? notMeasuredBlock(reasonOf(cleanedCheckResults.text_clustering))
                            : (
                              <>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                  <div style={{ ...bigNumberWrap, marginBottom: 0, textAlign: 'center' }}>
                                    <div style={bigNumberStyle('#8B5CF6')}>
                                      {(cleanedCheckResults.text_clustering.overall?.total_similar_pairs ?? cleanedCheckResults.text_clustering.total_similar_pairs ?? 0).toLocaleString()}
                                    </div>
                                    <div style={{ fontSize: '10px', color: isDarkMode ? '#a8a49a' : '#6B7280', textTransform: 'lowercase', letterSpacing: '0.06em', marginTop: '2px' }}>Similar Pairs</div>
                                  </div>
                                  <div style={{ ...bigNumberWrap, marginBottom: 0, textAlign: 'center' }}>
                                    <div style={bigNumberStyle('#D97706')}>
                                      {(cleanedCheckResults.text_clustering.overall?.total_outliers ?? cleanedCheckResults.text_clustering.total_outliers ?? 0).toLocaleString()}
                                    </div>
                                    <div style={{ fontSize: '10px', color: isDarkMode ? '#a8a49a' : '#6B7280', textTransform: 'lowercase', letterSpacing: '0.06em', marginTop: '2px' }}>Atypical Text</div>
                                  </div>
                                </div>
                              </>
                            )}
                        </div>
                      ),
                      cleanedCheckResults.sentinel_phrases && (
                        <div key="sent" style={cardWrapStyle}>
                          <h3 style={titleStyle}>
                            <MessageSquare size={14} style={{ color: '#EC4899' }} />
                            Sentinel Phrases
                          </h3>
                          {isNotRun(cleanedCheckResults.sentinel_phrases) ? notMeasuredBlock(reasonOf(cleanedCheckResults.sentinel_phrases))
                            : (
                              <>
                                <div style={bigNumberWrap}>
                                  <div style={bigNumberStyle('#EC4899')}>{flaggedCount(cleanedCheckResults.sentinel_phrases).toLocaleString()}</div>
                                  <div style={subtleLabel}>Short / Placeholder / Encoding Flags</div>
                                </div>
                                {flaggedTopN(cleanedCheckResults.sentinel_phrases, 5)}
                              </>
                            )}
                        </div>
                      ),
                      cleanedCheckResults.gated_fields && (
                        <div key="gate" style={cardWrapStyle}>
                          <h3 style={titleStyle}>
                            <LinkIcon size={14} style={{ color: '#A855F7' }} />
                            Gated Fields
                          </h3>
                          {isNotRun(cleanedCheckResults.gated_fields) ? notMeasuredBlock(reasonOf(cleanedCheckResults.gated_fields))
                            : (
                              <>
                                <div style={bigNumberWrap}>
                                  <div style={bigNumberStyle('#A855F7')}>{flaggedCount(cleanedCheckResults.gated_fields).toLocaleString()}</div>
                                  <div style={subtleLabel}>Gate / Follow-up Contradictions</div>
                                </div>
                                {flaggedTopN(cleanedCheckResults.gated_fields, 5)}
                              </>
                            )}
                        </div>
                      ),
                      cleanedCheckResults.probe_depth && (
                        <div key="probe" style={cardWrapStyle}>
                          <h3 style={titleStyle}>
                            <Target size={14} style={{ color: '#F97316' }} />
                            Probe Depth
                          </h3>
                          {isNotRun(cleanedCheckResults.probe_depth) ? notMeasuredBlock(reasonOf(cleanedCheckResults.probe_depth))
                            : (
                              <>
                                <div style={bigNumberWrap}>
                                  <div style={bigNumberStyle('#F97316')}>{flaggedCount(cleanedCheckResults.probe_depth).toLocaleString()}</div>
                                  <div style={subtleLabel}>Low-Specificity Responses</div>
                                </div>
                                {flaggedTopN(cleanedCheckResults.probe_depth, 5)}
                              </>
                            )}
                        </div>
                      ),
                      cleanedCheckResults.straight_lining && (
                        <div key="sl" style={cardWrapStyle}>
                          <h3 style={titleStyle}>
                            <Activity size={14} style={{ color: '#EF4444' }} />
                            Straight-Lining
                          </h3>
                          {isNotRun(cleanedCheckResults.straight_lining) ? notMeasuredBlock(reasonOf(cleanedCheckResults.straight_lining))
                            : (
                              <>
                                <div style={bigNumberWrap}>
                                  <div style={bigNumberStyle('#EF4444')}>{flaggedCount(cleanedCheckResults.straight_lining).toLocaleString()}</div>
                                  <div style={subtleLabel}>Respondents Flagged</div>
                                </div>
                                {flaggedTopN(cleanedCheckResults.straight_lining, 5)}
                              </>
                            )}
                        </div>
                      ),
                      // Backcheck — typically not_run_on_cleaned, render a card explaining why
                      cleanedCheckResults.backcheck && (
                        <div key="bc" style={cardWrapStyle}>
                          <h3 style={titleStyle}>
                            <Shuffle size={14} style={{ color: '#6366F1' }} />
                            Back-check
                          </h3>
                          {isNotRun(cleanedCheckResults.backcheck) ? notMeasuredBlock(reasonOf(cleanedCheckResults.backcheck) || 'requires separate back-check dataset')
                            : (
                              <div style={bigNumberWrap}>
                                <div style={bigNumberStyle('#6366F1')}>{flaggedCount(cleanedCheckResults.backcheck).toLocaleString()}</div>
                                <div style={subtleLabel}>Discrepancies</div>
                              </div>
                            )}
                        </div>
                      ),
                    ].filter(Boolean);

                    return detectorCards;
                  })()}
                </div>
              </div>
            ) : (
              <div style={styles.noDataContainer}>
                <AlertTriangle size={28} style={{ color: '#D97706', marginBottom: '14px', opacity: 0.85 }} />
                <p style={{ color: isDarkMode ? '#a8a49a' : '#6B7280' }}>
                  Run quality analysis first to see detailed results
                </p>
                <button
                  style={{
                    ...styles.actionButton,
                    backgroundColor: isDarkMode ? '#f0ede4' : '#1a1a1a',
                    color: isDarkMode ? '#0D0E0E' : '#F8F5EE',
                    marginTop: '16px'
                  }}
                  onClick={runCleanedAnalysis}
                >
                  <Play size={13} />
                  Run Quality Analysis
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CleanedDataReview;